import { useFormState } from 'informed';
import { useEffect, useState } from 'react';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import api from 'api';
import { Alert } from 'components/common';
import {
  DisplayField,
  Emails,
  FormGroup,
  GroupsSelector,
  RadioGroup,
  Usernames,
  UserSelectorNewUsers
} from 'components/common/form';

import type { UserWithBasicData } from 'shared-types';
import type { EmailsProps } from 'components/common/form/Emails';
import type { UsernamesProps } from 'components/common/form/Usernames';

type Selector = 'emails' | 'usernames' | 'groups';

interface Props extends Partial<EmailsProps>, Partial<UsernamesProps> {
  allowGroupSnapshots?: boolean;
  customField?: { email: string; username: string };
  enforceEmail?: boolean;
  enforceUsername?: boolean;
  id?: string;
  hideNewUserInformation?: boolean;
  preventNewUsers?: boolean;
  preventNewAccountsWithUsername?: boolean;
  onChange?: (values: string[]) => void;
  /**
   * Called with the users for whom an account is created on submit. Includes
   * guest accounts added by email address, since those are converted into
   * normal user accounts and need the same handling as completely new accounts.
   */
  onNewUsers?: (emails: string[]) => void;
  onUserSelectorChange?: (value: Selector) => void;
  required?: boolean;
  validate?: (emails: string[]) => string | undefined;
  validateOnChange?: boolean;
}

const UserSelector = ({
  allowGroupSnapshots = false,
  customField,
  enforceEmail = false,
  enforceUsername = false,
  id,
  hideNewUserInformation = false,
  preventNewUsers = false,
  preventNewAccountsWithUsername = false,
  onChange,
  onNewUsers,
  onUserSelectorChange,
  required = false,
  ...props
}: Props) => {
  const { values } = useFormState<{ usernames?: string[] }>();

  const {
    messages,
    serverConfig: {
      accountEnableUsernameAccounts,
      accountLimitUsernameCreationToSuperadmins
    },
    user: loggedInUser
  } = useReduxState();

  const [guestUsers, setGuestUsers] = useState<string[]>([]);
  const [newUsers, setNewUsers] = useState<string[]>([]);
  const [selector, setSelector] = useState<Selector>(
    accountEnableUsernameAccounts && enforceUsername ? 'usernames' : 'emails'
  );

  useEffect(() => {
    if (!newUsers.length) return;
    if (!values.usernames?.length && newUsers.length) setNewUsers([]);

    const changedNewUsers = newUsers.filter((user) =>
      values.usernames.includes(user)
    );
    if (changedNewUsers.length !== newUsers.length) {
      setNewUsers(changedNewUsers);
    }
  }, [values.usernames]);

  useEffect(() => {
    if (onNewUsers) onNewUsers([...newUsers, ...guestUsers].sort());
  }, [guestUsers, newUsers]);

  const getUsersWithoutAccount = async (users: string[]) => {
    try {
      const response = await api.post('/admin/users-existing', {
        ...(selector === 'emails' ? { emails: users } : { usernames: users })
      });

      const existingUsers: { [identifier: string]: UserWithBasicData } =
        selector === 'emails'
          ? response.data.usersByEmail
          : response.data.usersByUsername;

      return {
        guestUsers:
          selector === 'emails'
            ? users.filter((user) =>
                existingUsers[user]?.roles.includes('guest')
              )
            : [],
        newUsers: users.filter((user) => !existingUsers[user])
      };
    } catch (error) {
      return { guestUsers: [], newUsers: [] };
    }
  };

  const handleChange = (values: string[]) => {
    if (onChange) onChange(values);
    if (selector === 'groups') return;

    if (!values) {
      setGuestUsers([]);
      setNewUsers([]);
    } else {
      const guests = guestUsers.filter((user) => values.includes(user));
      if (guests.length !== guestUsers.length) setGuestUsers(guests);

      const users = newUsers.filter((user) => values.includes(user));
      if (users.length !== newUsers.length) setNewUsers(users);
    }
  };

  const handleNewUsers = async (users: string[]) => {
    if (selector === 'groups') return;

    const usersWithoutAccount = await getUsersWithoutAccount(users);
    setGuestUsers(usersWithoutAccount.guestUsers.sort());
    setNewUsers(usersWithoutAccount.newUsers.sort());
  };

  const handleSelectorChange = (value: Selector) => {
    setGuestUsers([]);
    setNewUsers([]);
    setSelector(value);
    if (onUserSelectorChange) onUserSelectorChange(value);
  };

  return (
    <div>
      {allowGroupSnapshots ||
      (accountEnableUsernameAccounts && !enforceEmail && !enforceUsername) ? (
        <RadioGroup
          field="userSelector"
          id="userSelector"
          initialValue={selector}
          onChange={handleSelectorChange}
          options={[
            ...(accountEnableUsernameAccounts && enforceUsername
              ? []
              : [
                  {
                    label: messages.userSelectorEmail,
                    value: 'emails',
                    id: `selector-email${id ? `-${id}` : ''}`
                  }
                ]),
            ...(accountEnableUsernameAccounts && !enforceEmail
              ? [
                  {
                    label: messages.userSelectorUsername,
                    value: 'usernames',
                    id: `selector-username${id ? `-${id}` : ''}`
                  }
                ]
              : []),
            ...(allowGroupSnapshots
              ? [
                  {
                    label: messages.settingsGroupsUsers,
                    value: 'groups',
                    id: `selector-groups${id ? `-${id}` : ''}`
                  }
                ]
              : [])
          ]}
          style={{ marginBottom: 8 }}
        />
      ) : (
        <DisplayField field="userSelector" />
      )}

      <FormGroup
        label={
          selector === 'emails'
            ? messages.emailAddresses
            : selector === 'usernames'
            ? messages.commonUsernames
            : messages.commonGroups
        }
        labelTooltip={
          selector === 'groups' ? '' : messages.emailAddressesTooltip
        }
        maxWidth="100%"
      >
        {selector === 'groups' ? (
          <GroupsSelector />
        ) : selector === 'usernames' ? (
          <Usernames
            countNewUsers={hideNewUserInformation ? 0 : newUsers.length}
            disableCreatingNewAccounts={
              preventNewUsers ||
              preventNewAccountsWithUsername ||
              (accountLimitUsernameCreationToSuperadmins &&
                !loggedInUser?.roles.includes('superadmin'))
            }
            field={customField?.username ?? 'usernames'}
            id={customField?.username ?? 'usernames'}
            onChange={handleChange}
            onUserChange={handleNewUsers}
            {...(required
              ? { isRequired: true, required: messages.commonFieldRequired }
              : {})}
            {...props}
          />
        ) : (
          <Emails
            countNewUsers={hideNewUserInformation ? 0 : newUsers.length}
            disableCreatingNewAccounts={preventNewUsers}
            field={customField?.email ?? 'emails'}
            id={customField?.email ?? 'emails'}
            onChange={handleChange}
            onUserChange={handleNewUsers}
            {...(required
              ? { isRequired: true, required: messages.commonFieldRequired }
              : {})}
            {...props}
          />
        )}
      </FormGroup>

      {!hideNewUserInformation && guestUsers.length && selector === 'emails' ? (
        <Alert
          size="small"
          style={{ marginTop: 10 }}
          text={getMessageWithPlaceholders({
            message: messages.userSelectorGuestConversionInfo,
            placeholders: {
              emails: guestUsers.join(', ')
            }
          })}
        />
      ) : null}

      {!hideNewUserInformation &&
      newUsers.length &&
      selector === 'usernames' ? (
        <UserSelectorNewUsers users={newUsers} />
      ) : null}
    </div>
  );
};

export default UserSelector;
